import { useEffect, useState } from "react";
import CodeBlock from "@/components/base/CodeBlock";
import { formatFullDate } from "@/lib/format";

interface ApiDebugPanelProps {
  endpoint: string;
  apiKey: string;
}

interface DebugPreset {
  id: string;
  label: string;
  licenseKey: string;
  machineCode: string;
  product: string;
}

interface DebugHistoryEntry {
  id: string;
  licenseKey: string;
  machineCode: string;
  product: string;
  valid: boolean;
  reason: string;
  status: number;
  response: Record<string, unknown>;
  at: string;
}

const STORAGE_KEY = "lh_debug_presets";
const HISTORY_KEY = "lh_debug_history";
const HISTORY_MAX_KEY = "lh_debug_history_max";
const MAX_PRESETS = 20;
const HISTORY_MAX_OPTIONS = [12, 30, 100];
const DEFAULT_HISTORY_MAX = 12;

function loadPresets(): DebugPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as DebugPreset[]).filter((item) => item && typeof item.id === "string").slice(0, MAX_PRESETS);
  } catch {
    return [];
  }
}

// 读取用户配置的历史保留条数上限，非法值回退到默认。
function loadHistoryMax(): number {
  try {
    const raw = localStorage.getItem(HISTORY_MAX_KEY);
    const parsed = Number(raw);
    if (HISTORY_MAX_OPTIONS.includes(parsed)) return parsed;
  } catch {
    // 本地存储不可用时用默认值
  }
  return DEFAULT_HISTORY_MAX;
}

function loadHistory(max: number): DebugHistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as DebugHistoryEntry[]).filter((item) => item && typeof item.id === "string").slice(0, max);
  } catch {
    return [];
  }
}

function makeId(): string {
  return `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

// 生成一个符合示例格式的随机机器码（XXXX-XXXX-XXXX-XXXX），没有真实机器码时也能跑通校验测试。
function randomMachineCode(): string {
  const hex = "0123456789ABCDEF";
  const group = () => {
    let out = "";
    for (let i = 0; i < 4; i += 1) out += hex[Math.floor(Math.random() * hex.length)];
    return out;
  };
  return [group(), group(), group(), group()].join("-");
}

function autoLabel(licenseKey: string, product: string): string {
  const productName = product.trim();
  if (productName) return productName;
  const tail = licenseKey.trim().replace(/\s/g, "").slice(-6);
  return tail ? `测试密钥 …${tail}` : "未命名测试组";
}

// 把一次历史调用还原成可直接发送的 cURL 命令行（含端点 / apikey / JSON body）。
function buildCurl(endpoint: string, apiKey: string, integrationKey: string, entry: DebugHistoryEntry): string {
  const payload: Record<string, string> = { licenseKey: entry.licenseKey, machineCode: entry.machineCode };
  if (entry.product) payload.product = entry.product;
  const data = JSON.stringify(payload).replace(/'/g, "'\\''");
  return [
    `curl -X POST '${endpoint}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -H 'apikey: ${apiKey}' \\`,
    `  -H 'x-api-key: ${integrationKey || "<你的API密钥>"}' \\`,
    `  -d '${data}'`,
  ].join("\n");
}

// 把多条历史调用整段导出为可粘贴的文本（逐条分隔，附索引 / 结果 / 时间），便于一次贴进工单。
function buildAllCurl(endpoint: string, apiKey: string, integrationKey: string, entries: DebugHistoryEntry[]): string {
  const header = `# LicenseHub 在线调试 · 共 ${entries.length} 条调用记录\n# 端点：${endpoint}`;
  const blocks = entries.map((entry, index) => {
    const meta = `# [${index + 1}] ${entry.machineCode} · ${entry.valid ? "valid" : "invalid"}${
      entry.reason ? ` · ${entry.reason}` : ""
    } · ${entry.at}`;
    return `${meta}\n${buildCurl(endpoint, apiKey, integrationKey, entry)}`;
  });
  return `${header}\n\n${blocks.join("\n\n")}\n`;
}

// 把整段历史导出为结构化 JSON，方便直接附件或粘贴到工单。
function buildAllJson(endpoint: string, entries: DebugHistoryEntry[]): string {
  return `${JSON.stringify(
    {
      endpoint,
      exportedAt: new Date().toISOString(),
      total: entries.length,
      calls: entries,
    },
    null,
    2,
  )}\n`;
}

// 文档页在线调试：直接填密钥 + 机器码调一次校验接口，实时查看 valid 与 reason；
// 支持把多组测试密钥存到本地，一键切换不同产品 / 场景反复验证。
export default function ApiDebugPanel({ endpoint, apiKey }: ApiDebugPanelProps) {
  const [licenseKey, setLicenseKey] = useState("");
  const [machineCode, setMachineCode] = useState("");
  const [product, setProduct] = useState("");
  const [integrationKey, setIntegrationKey] = useState<string>(() => {
    try {
      return localStorage.getItem("lh_debug_apikey") ?? "";
    } catch {
      return "";
    }
  });
  const [running, setRunning] = useState(false);
  const [response, setResponse] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [presets, setPresets] = useState<DebugPreset[]>(() => loadPresets());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [labelInput, setLabelInput] = useState("");
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [historyMax, setHistoryMax] = useState<number>(() => loadHistoryMax());
  const [history, setHistory] = useState<DebugHistoryEntry[]>(() => loadHistory(loadHistoryMax()));
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState<"curl" | "json" | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
    } catch {
      // 本地存储不可用时静默失败，不影响调试主流程
    }
  }, [presets]);

  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch {
      // 本地存储不可用时静默失败，不影响调试主流程
    }
  }, [history]);

  useEffect(() => {
    try {
      localStorage.setItem("lh_debug_apikey", integrationKey);
    } catch {
      // 本地存储不可用时静默失败，不影响调试主流程
    }
  }, [integrationKey]);

  // 持久化保留上限；调低时同步裁剪已有记录，保证「上限」名副其实。
  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_MAX_KEY, String(historyMax));
    } catch {
      // 本地存储不可用时静默失败
    }
    setHistory((prev) => (prev.length > historyMax ? prev.slice(0, historyMax) : prev));
  }, [historyMax]);

  const flash = (text: string) => {
    setSaveNotice(text);
    window.setTimeout(() => setSaveNotice(null), 2200);
  };

  const editField = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setActiveId(null);
    if (error) setError(null);
  };

  const applyPreset = (preset: DebugPreset) => {
    setLicenseKey(preset.licenseKey);
    setMachineCode(preset.machineCode);
    setProduct(preset.product);
    setActiveId(preset.id);
    setError(null);
    setResponse(null);
  };

  const saveCurrent = () => {
    const key = licenseKey.trim();
    const machine = machineCode.trim();
    if (!key || !machine) {
      setError("请先填写许可证密钥与机器码，再保存测试组");
      return;
    }
    const label = labelInput.trim() || autoLabel(key, product);
    const next: DebugPreset = { id: makeId(), label, licenseKey: key, machineCode: machine, product: product.trim() };
    setPresets((prev) => [next, ...prev].slice(0, MAX_PRESETS));
    setActiveId(next.id);
    setLabelInput("");
    flash(`已保存测试组「${label}」`);
  };

  const deletePreset = (id: string) => {
    setPresets((prev) => prev.filter((item) => item.id !== id));
    if (activeId === id) setActiveId(null);
  };

  // 载入一条历史记录：回填机器码 / 密钥 / 返回结果，方便和这一次的调用做对比。
  const applyHistory = (entry: DebugHistoryEntry) => {
    setLicenseKey(entry.licenseKey);
    setMachineCode(entry.machineCode);
    setProduct(entry.product);
    setActiveId(null);
    setError(null);
    setResponse(entry.response);
  };

  // 把某条历史调用导出为可直接发送的 cURL 命令并复制到剪贴板。
  const exportAsCurl = async (entry: DebugHistoryEntry) => {
    try {
      await navigator.clipboard.writeText(buildCurl(endpoint, apiKey, integrationKey, entry));
      setCopiedId(entry.id);
      window.setTimeout(() => setCopiedId((prev) => (prev === entry.id ? null : prev)), 2000);
    } catch {
      setError("复制失败，请手动选择复制");
    }
  };

  // 整段导出全部历史调用（多条 cURL / JSON），一次性复制便于贴到工单里。
  const exportAll = async (format: "curl" | "json") => {
    if (history.length === 0) {
      setError("暂无调用记录可导出");
      return;
    }
    const text = format === "curl" ? buildAllCurl(endpoint, apiKey, integrationKey, history) : buildAllJson(endpoint, history);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAll(format);
      window.setTimeout(() => setCopiedAll((prev) => (prev === format ? null : prev)), 2000);
    } catch {
      setError("复制失败，请手动选择复制");
    }
  };

  const run = async () => {
    if (!integrationKey.trim()) {
      setError("请填写 API 密钥（x-api-key），可在「对接方管理」为应用签发");
      setResponse(null);
      return;
    }
    const key = licenseKey.trim();
    const machine = machineCode.trim();
    if (!key || !machine) {
      setError("请填写许可证密钥与机器码");
      setResponse(null);
      return;
    }
    setError(null);
    setRunning(true);
    setResponse(null);
    try {
      const payload: Record<string, string> = { licenseKey: key, machineCode: machine };
      if (product.trim()) payload.product = product.trim();

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: apiKey, "x-api-key": integrationKey.trim() },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(text) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      if (!parsed) {
        setError(`接口返回无法解析（HTTP ${res.status}）：${text.slice(0, 160)}`);
        return;
      }
      setResponse(parsed);
      const entry: DebugHistoryEntry = {
        id: makeId(),
        licenseKey: key,
        machineCode: machine,
        product: product.trim(),
        valid: parsed.valid === true,
        reason: typeof parsed.reason === "string" ? parsed.reason : "",
        status: res.status,
        response: parsed,
        at: new Date().toISOString(),
      };
      setHistory((prev) => [entry, ...prev].slice(0, historyMax));
    } catch (err) {
      setError(err instanceof Error ? err.message : "请求失败，请稍后重试");
    } finally {
      setRunning(false);
    }
  };

  const valid = response?.valid === true;
  const reason = typeof response?.reason === "string" ? response.reason : "";

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
          <i className="ri-terminal-box-line" />
        </span>
        <div>
          <h3 className="font-heading text-base font-semibold text-foreground-950">在线调试</h3>
          <p className="text-xs text-foreground-500">填入真实密钥与机器码，直接调一次上面的校验接口，实时查看返回结果。</p>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-background-200 bg-background-100 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-foreground-700">
            <span className="flex h-4 w-4 items-center justify-center text-secondary-800">
              <i className="ri-bookmark-3-line" />
            </span>
            测试密钥组
            <span className="text-foreground-400">（保存到本地，可一键切换）</span>
          </span>
          {presets.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setPresets([]);
                setActiveId(null);
              }}
              className="cursor-pointer text-[11px] text-foreground-500 transition-colors hover:text-accent-700"
            >
              清空全部
            </button>
          )}
        </div>

        {presets.length === 0 ? (
          <p className="mt-3 text-xs text-foreground-500">
            还没有保存的测试组。填好一组密钥后，点下方「保存当前」即可存起来，下次一键切换复用。
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {presets.map((preset) => {
              const active = preset.id === activeId;
              return (
                <span
                  key={preset.id}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                    active
                      ? "border-primary-300 bg-primary-100 text-primary-700"
                      : "border-background-300 bg-background-50 text-foreground-700"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap"
                    title={preset.licenseKey}
                  >
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className={active ? "ri-checkbox-circle-line" : "ri-key-2-line"} />
                    </span>
                    {preset.label}
                  </button>
                  <button
                    type="button"
                    onClick={() => deletePreset(preset.id)}
                    aria-label={`删除 ${preset.label}`}
                    className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-200 hover:text-accent-700"
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="text"
            value={labelInput}
            onChange={(event) => setLabelInput(event.target.value)}
            placeholder="测试组名称（可选，留空自动命名）"
            className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={saveCurrent}
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-save-line" />
            </span>
            保存当前
          </button>
        </div>
        {saveNotice && <p className="mt-2 text-[11px] text-primary-700">{saveNotice}</p>}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <label className="flex flex-col gap-1.5 lg:col-span-3">
          <span className="text-xs font-medium text-foreground-700">API 密钥（x-api-key）*</span>
          <input
            type="text"
            value={integrationKey}
            onChange={(event) => setIntegrationKey(event.target.value)}
            placeholder="lh_live_xxxxxxxxxxxxxxxx"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 font-mono text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />
          <span className="text-[11px] text-foreground-500">
            在「管理后台 → 对接方管理」为你的应用签发一把 API Key 后填入，调试会校验它并计入调用日志。
          </span>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-foreground-700">许可证密钥 *</span>
          <input
            type="text"
            value={licenseKey}
            onChange={(event) => editField(setLicenseKey)(event.target.value)}
            placeholder="LH-XXXX-XXXX-XXXX-XXXX"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 font-mono text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="flex items-center justify-between text-xs font-medium text-foreground-700">
            机器码 *
            <button
              type="button"
              onClick={() => editField(setMachineCode)(randomMachineCode())}
              title="生成一个随机机器码用于快速测试"
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2 py-0.5 text-[11px] font-normal text-foreground-600 transition-colors hover:bg-background-100"
            >
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-shuffle-line" />
              </span>
              随机生成
            </button>
          </span>
          <input
            type="text"
            value={machineCode}
            onChange={(event) => editField(setMachineCode)(event.target.value)}
            placeholder="A1B2-C3D4-E5F6-7890"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 font-mono text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-foreground-700">软件名（可选）</span>
          <input
            type="text"
            value={product}
            onChange={(event) => editField(setProduct)(event.target.value)}
            placeholder="填入后会校验产品是否匹配"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={running}
          onClick={() => void run()}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={running ? "ri-loader-4-line animate-spin" : "ri-play-line"} />
          </span>
          {running ? "请求中…" : "发起校验"}
        </button>
        <span className="text-[11px] text-foreground-500">
          调试同样会真实调用接口，可能触发「首次绑机」与打开次数累计，请使用测试密钥。
        </span>
      </div>

      {error && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5 text-xs text-accent-800">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-error-warning-line" />
          </span>
          {error}
        </div>
      )}

      {response && (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                valid ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-800"
              }`}
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className={valid ? "ri-checkbox-circle-line" : "ri-close-circle-line"} />
              </span>
              valid: {String(valid)}
            </span>
            {reason && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-200 px-3 py-1 font-mono text-xs text-foreground-700">
                reason: {reason}
              </span>
            )}
            {typeof response.message === "string" && (
              <span className="text-xs text-foreground-600">{response.message}</span>
            )}
          </div>
          <div className="mt-3">
            <CodeBlock label="实时返回" code={JSON.stringify(response, null, 2)} />
          </div>
        </div>
      )}

      <div className="mt-5 rounded-md border border-background-200 bg-background-100 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-foreground-700">
            <span className="flex h-4 w-4 items-center justify-center text-secondary-800">
              <i className="ri-history-line" />
            </span>
            历史调用记录
            <span className="text-foreground-400">（保存在本地，方便对比不同机器码的返回）</span>
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[11px] text-foreground-500">保留上限</span>
              <span className="inline-flex items-center gap-0.5 rounded-full border border-background-300 bg-background-50 p-1">
                {HISTORY_MAX_OPTIONS.map((option) => {
                  const active = option === historyMax;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setHistoryMax(option)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                        active ? "bg-secondary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {option} 条
                    </button>
                  );
                })}
              </span>
            </span>
            {history.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => void exportAll("curl")}
                  title="把全部历史调用拼成一段 cURL 文本并复制"
                  className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-2.5 py-1 text-[11px] font-medium text-primary-700 transition-colors hover:bg-primary-100"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className={copiedAll === "curl" ? "ri-checkbox-circle-line" : "ri-code-s-slash-line"} />
                  </span>
                  {copiedAll === "curl" ? "已复制 cURL" : "导出全部 cURL"}
                </button>
                <button
                  type="button"
                  onClick={() => void exportAll("json")}
                  title="把全部历史调用导出为一段 JSON 文本并复制"
                  className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className={copiedAll === "json" ? "ri-checkbox-circle-line" : "ri-braces-line"} />
                  </span>
                  {copiedAll === "json" ? "已复制 JSON" : "导出全部 JSON"}
                </button>
                <button
                  type="button"
                  onClick={() => setHistory([])}
                  className="cursor-pointer text-[11px] text-foreground-500 transition-colors hover:text-accent-700"
                >
                  清空记录
                </button>
              </>
            )}
          </div>
        </div>

        {history.length === 0 ? (
          <p className="mt-3 text-xs text-foreground-500">
            还没有调用记录。发起一次校验后，机器码与返回结果会自动留在这里，点任意一条可载入复看。
          </p>
        ) : (
          <ul className="mt-3 flex flex-col gap-2">
            {history.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-col gap-2 rounded-md border border-background-200 bg-background-50 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        entry.valid ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-800"
                      }`}
                    >
                      <span className="flex h-3 w-3 items-center justify-center">
                        <i className={entry.valid ? "ri-checkbox-circle-line" : "ri-close-circle-line"} />
                      </span>
                      {entry.valid ? "valid" : "invalid"}
                    </span>
                    <span className="font-mono text-xs text-foreground-900">{entry.machineCode}</span>
                    {entry.reason && (
                      <span className="rounded-full bg-background-200 px-2 py-0.5 font-mono text-[11px] text-foreground-600">
                        {entry.reason}
                      </span>
                    )}
                    <span className="text-[11px] text-foreground-400">HTTP {entry.status}</span>
                  </div>
                  <span className="text-[11px] text-foreground-500">
                    {entry.product || "未填软件名"} · {formatFullDate(entry.at)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => applyHistory(entry)}
                    className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-arrow-go-back-line" />
                    </span>
                    载入对比
                  </button>
                  <button
                    type="button"
                    onClick={() => void exportAsCurl(entry)}
                    title="复制这条调用对应的 cURL 命令，可直接粘贴到终端发送"
                    className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
                  >
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className={copiedId === entry.id ? "ri-checkbox-circle-line" : "ri-code-s-slash-line"} />
                    </span>
                    {copiedId === entry.id ? "已复制" : "导出为 cURL"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}